/* ==========================================================================
   Amora theme behaviour
   - Sticky header that solidifies on scroll
   - Mobile navigation drawer
   - Single-open FAQ accordions
   - Reveal-on-scroll
   All initialisers are idempotent and re-run on Shopify section events so the
   theme editor stays in sync.
   ========================================================================== */

(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- header */

  function initHeader(scope) {
    var headers = (scope || document).querySelectorAll('[data-am-header]');

    Array.prototype.forEach.call(headers, function (header) {
      if (header.dataset.amHeaderReady === 'true') return;
      header.dataset.amHeaderReady = 'true';

      var threshold = parseInt(header.dataset.amScrollThreshold, 10);
      if (isNaN(threshold)) threshold = 24;

      var ticking = false;

      var apply = function () {
        var scrolled = window.pageYOffset > threshold;
        header.classList.toggle('am-header--scrolled', scrolled);
        ticking = false;
      };

      var onScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(apply);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      apply();

      // Expose the header height so overlay heroes can offset themselves.
      var setHeight = function () {
        document.documentElement.style.setProperty('--am-header-h', header.offsetHeight + 'px');
      };
      setHeight();
      window.addEventListener('resize', setHeight, { passive: true });
    });
  }

  /* ---------------------------------------------------------------- drawer */

  function initDrawer(scope) {
    var drawers = (scope || document).querySelectorAll('[data-am-drawer]');

    Array.prototype.forEach.call(drawers, function (drawer) {
      if (drawer.dataset.amDrawerReady === 'true') return;
      drawer.dataset.amDrawerReady = 'true';

      var id = drawer.getAttribute('id');
      var openers = document.querySelectorAll('[data-am-drawer-open="' + id + '"]');
      var closers = drawer.querySelectorAll('[data-am-drawer-close]');
      var lastFocused = null;

      function open() {
        lastFocused = document.activeElement;
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        var focusable = drawer.querySelector('a, button, input, select');
        if (focusable) focusable.focus();
        Array.prototype.forEach.call(openers, function (o) {
          o.setAttribute('aria-expanded', 'true');
        });
      }

      function close() {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        Array.prototype.forEach.call(openers, function (o) {
          o.setAttribute('aria-expanded', 'false');
        });
        if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      }

      Array.prototype.forEach.call(openers, function (opener) {
        opener.addEventListener('click', function (event) {
          event.preventDefault();
          open();
        });
      });

      Array.prototype.forEach.call(closers, function (closer) {
        closer.addEventListener('click', function (event) {
          event.preventDefault();
          close();
        });
      });

      drawer.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') close();
      });

      // Close when a nav link is followed.
      Array.prototype.forEach.call(drawer.querySelectorAll('a[href]'), function (link) {
        link.addEventListener('click', function () {
          close();
        });
      });
    });
  }

  /* ------------------------------------------------------------------- faq */

  function initAccordions(scope) {
    var groups = (scope || document).querySelectorAll('[data-am-accordion]');

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.amAccordionReady === 'true') return;
      group.dataset.amAccordionReady = 'true';

      if (group.dataset.amAccordion !== 'single') return;

      var items = group.querySelectorAll('details');
      Array.prototype.forEach.call(items, function (item) {
        item.addEventListener('toggle', function () {
          if (!item.open) return;
          Array.prototype.forEach.call(items, function (other) {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  }

  /* ------------------------------------------------- before/after slider */

  function initCompare(scope) {
    var sliders = (scope || document).querySelectorAll('[data-am-compare]');

    Array.prototype.forEach.call(sliders, function (slider) {
      if (slider.dataset.amCompareReady === 'true') return;
      slider.dataset.amCompareReady = 'true';

      var range = slider.querySelector('.am-ba__range');
      if (!range) return;

      var apply = function () {
        slider.style.setProperty('--am-ba-pos', range.value + '%');
        range.setAttribute('aria-valuenow', range.value);
      };

      range.addEventListener('input', apply);
      range.addEventListener('change', apply);
      apply();
    });
  }

  /* ---------------------------------------------------------------- reveal */

  function initReveal(scope) {
    var nodes = (scope || document).querySelectorAll('.am-reveal:not(.is-in)');
    if (!nodes.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, function (node) {
        node.classList.add('is-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
    );

    Array.prototype.forEach.call(nodes, function (node) {
      observer.observe(node);
    });
  }

  /* ------------------------------------------------------------------ boot */

  function initAll(scope) {
    initHeader(scope);
    initDrawer(scope);
    initAccordions(scope);
    initCompare(scope);
    initReveal(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    initAll(event.target);
  });
})();
